import React, { useState } from 'react';
import ModalWrapper from './ModalWrapper';
import './CallOffModal.css';

const CallOffModal = ({ isOpen, onClose, stockItem, onCallOffCreated }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get tomorrow's date as minimum booking date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/call-offs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stockItemId: stockItem.id,
          orderNumber,
          qty: parseInt(qty),
          note,
          bookingDate
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create call-off');
      }

      const callOff = await response.json();
      onCallOffCreated(callOff);
      
      // Reset form
      setOrderNumber('');
      setQty('');
      setNote('');
      setBookingDate('');
      onClose();
    } catch (error) {
      console.error('Error creating call-off:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper open={isOpen} onClose={onClose} title="Create Call-Off Order">
      <div className="call-off-modal">

        <div className="stock-info">
          <h3>{stockItem?.productCode}</h3>
          <p>{stockItem?.variantDesc}</p>
          <div className="stock-details">
            <span>Current Stock: {stockItem?.currentQty}</span>
            <span>Client: {stockItem?.client?.name || 'General Stock'}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="call-off-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="orderNumber">Work Ticket Number *</label>
              <input
                type="text"
                id="orderNumber"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                required
                placeholder="Enter work ticket number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="qty">Quantity *</label>
              <input
                type="number"
                id="qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                required
                min="1"
                placeholder="Enter quantity"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="bookingDate">Booking Date *</label>
            <input
              type="date"
              id="bookingDate"
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
              required
              min={getTomorrowDate()}
            />
            <small className="form-help">
              Select the date when this call-off should be converted to a booking
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="note">Note</label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note about this call-off"
              rows="3"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Creating...' : 'Create Call-Off'}
            </button>
          </div>
        </form>

        <div className="call-off-info">
          <h4>About Call-Off Orders</h4>
          <ul>
            <li>Call-offs are advance orders that don't affect current stock</li>
            <li>They automatically convert to bookings on the specified date</li>
            <li>Use regular bookings for immediate dispatch needs</li>
            <li>Call-offs can be manually converted early if needed</li>
          </ul>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CallOffModal;
